import { useState } from "react";
import { Loader2 } from "lucide-react";

import { ErrorBanner } from "@/components/common/FeatureShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type ProjectRow } from "@/lib/api";
import { useCurrentOrg } from "@/contexts/OrgContext";
import { useProjectCreateMutation } from "@/hooks/queries/useProjects";

function parseEmails(value: string) {
  return value
    .split(/[,\n]/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

export function NewProjectModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (project: ProjectRow) => void }) {
  const [name, setName] = useState("");
  const [cmNumber, setCmNumber] = useState("");
  const [emails, setEmails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { currentOrgId } = useCurrentOrg();
  const createMutation = useProjectCreateMutation();
  const saving = createMutation.isPending;

  const submit = async () => {
    if (!name.trim()) {
      setError("Enter a matter name.");
      return;
    }

    setError(null);
    try {
      const project = await createMutation.mutateAsync({
        name: name.trim(),
        cm_number: cmNumber.trim() || undefined,
        shared_with: parseEmails(emails),
        // Land the project in the active org; the backend assigns the caller's
        // default org when org_id is omitted.
        org_id: currentOrgId ?? undefined,
      });
      onCreated(project);
      setName("");
      setCmNumber("");
      setEmails("");
      onClose();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Could not create the matter.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New matter</DialogTitle>
          <DialogDescription>Create a workspace for documents and assistant chats.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto">
          <ErrorBanner message={error} />
        <div className="space-y-2">
          <Label htmlFor="project-name">Name</Label>
          <Input id="project-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Matter or client name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="project-cm">CM number</Label>
          <Input id="project-cm" value={cmNumber} onChange={(event) => setCmNumber(event.target.value)} placeholder="Optional" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="project-emails">Collaborators</Label>
          <Textarea
            id="project-emails"
            value={emails}
            onChange={(event) => setEmails(event.target.value)}
            rows={3}
            placeholder="email@example.com, colleague@example.com"
          />
        </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
